import dayjs from 'dayjs';
import { t } from '@lingui/macro';

import { i18n } from '@lib/i18n';

import { weekDays, monthlyWeeks, defaultRecurrence } from '../datas';

function labelForWeekday(value) {
  const found = weekDays.find(day => day.value === Number(value));
  return found ? found.label : '';
}

function labelForWeek(value) {
  const found = monthlyWeeks.find(week => week.value === Number(value));
  return found ? found.label : '';
}

/**
 * Joins weekday indices into a human list, e.g. "Monday, Wednesday and Friday".
 * @param {number[]} days indices using 0 = Monday … 6 = Sunday
 */
function joinWeekdays(days = []) {
  const labels = [...days]
    .map(Number)
    .sort((a, b) => a - b)
    .map(labelForWeekday)
    .filter(Boolean);

  if (labels.length === 0) return '';
  if (labels.length === 1) return labels[0];

  const and = i18n._(t`and`);
  if (labels.length === 2) return `${labels[0]} ${and} ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')} ${and} ${
    labels[labels.length - 1]
  }`;
}

function frequencyClause(recurrence) {
  const interval = Math.max(1, parseInt(recurrence.interval, 10) || 1);
  const many = interval > 1;

  if (recurrence.frequency === 'daily') {
    return many ? i18n._(t`every ${interval} days`) : i18n._(t`every day`);
  }

  if (recurrence.frequency === 'weekly') {
    const base = many
      ? i18n._(t`every ${interval} weeks`)
      : i18n._(t`every week`);
    const days = joinWeekdays(recurrence.weeklyDays);
    return days ? `${base} ${i18n._(t`on`)} ${days}` : base;
  }

  if (recurrence.frequency === 'monthly') {
    const base = many
      ? i18n._(t`every ${interval} months`)
      : i18n._(t`every month`);

    if (recurrence.monthlyMode === 'weekday_pattern') {
      const week = labelForWeek(recurrence.monthlyWeek);
      const weekday = labelForWeekday(recurrence.monthlyWeekday);
      return week && weekday
        ? `${base} ${i18n._(t`on the`)} ${week} ${weekday}`
        : base;
    }

    const day = recurrence.monthlyDay;
    return day ? `${base} ${i18n._(t`on day ${day}`)}` : base;
  }

  return '';
}

function endClause(recurrence) {
  if (recurrence.endType === 'on_date' && recurrence.endDate) {
    return ` ${i18n._(t`until`)} ${dayjs(recurrence.endDate).format('ll')}`;
  }
  if (recurrence.endType === 'after_occurrences') {
    const occurrences = Math.max(1, parseInt(recurrence.occurrences, 10) || 1);
    return ` ${i18n._(t`for ${occurrences} occurrences`)}`;
  }
  return '';
}

/**
 * Builds the live, human-readable preview of a recurrence rule, e.g.
 * "This task will repeat every week on Monday and Friday until Aug 30".
 * Pure + defensive: tolerates partially-filled config.
 */
export function getRecurrencePreview(recurrence) {
  if (!recurrence || !recurrence.frequency) return '';
  const clause = frequencyClause(recurrence);
  if (!clause) return '';
  return `${i18n._(t`This task will repeat`)} ${clause}${endClause(
    recurrence,
  )}`;
}

/**
 * Reduces the form recurrence object to the minimal payload the API expects:
 * drops fields irrelevant to the chosen frequency / mode / end type and
 * normalizes types. Keys stay camelCase — the fetcher snake_cases them.
 */
export function buildRecurrencePayload(recurrence) {
  if (!recurrence) return undefined;

  const payload = {
    frequency: recurrence.frequency,
    interval: Math.max(1, parseInt(recurrence.interval, 10) || 1),
    endType: recurrence.endType,
  };

  if (recurrence.frequency === 'weekly') {
    payload.weeklyDays = [...(recurrence.weeklyDays || [])]
      .map(Number)
      .sort((a, b) => a - b);
  }

  if (recurrence.frequency === 'monthly') {
    payload.monthlyMode = recurrence.monthlyMode;
    if (recurrence.monthlyMode === 'weekday_pattern') {
      payload.monthlyWeek = Number(recurrence.monthlyWeek);
      payload.monthlyWeekday = Number(recurrence.monthlyWeekday);
    } else {
      payload.monthlyDay = Number(recurrence.monthlyDay);
    }
  }

  if (recurrence.endType === 'on_date') {
    payload.endDate = recurrence.endDate
      ? dayjs(recurrence.endDate).format('YYYY-MM-DD')
      : null;
  } else if (recurrence.endType === 'after_occurrences') {
    payload.occurrences = Math.max(
      1,
      parseInt(recurrence.occurrences, 10) || 1,
    );
  }

  return payload;
}

/**
 * Maps a task response (camelCased) into recurrence form initial values for
 * the edit flow. Falls back to sensible defaults seeded from the due date.
 * @param {object} task
 */
export function recurrenceFromTask(task) {
  const base = defaultRecurrence(task && task.dueDate);

  if (!task || !task.isRecurring || !task.recurrence) {
    return { repeat: false, recurrence: base };
  }

  const r = task.recurrence;
  return {
    repeat: true,
    recurrence: {
      ...base,
      ...r,
      endDate: r.endDate ? new Date(r.endDate) : null,
    },
  };
}
